import React, { useCallback, useEffect, useMemo } from "react";
//@ts-ignore
import FileIcon from "../../../../assets/images/file.png";
//@ts-ignore
import { ReactComponent as Success } from "../../../../assets/svg/success.svg";
import { Check, Plus, Search } from "lucide-react";
import CustomInput from "../../../../components/CustomInput/CustomInput";
import { useForm } from "react-hook-form";
import LoadingAnimation from "../../../../assets/svg/LoadingAnimation";
import { colors } from "../../../../utils/theme";
import { isNullOrEmpty } from "../../../../utils/helperFunction";
import {
  getJobRequirement,
  getPartnerAgencies,
  grantAccess,
} from "../../../../endpoints/hiringCompany/requirements";
import {
  PartnerCompaniesFields,
  PartnerCompany,
} from "./partnerAgencies.types";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import CrossBig from "../../../../assets/svg/CrossBig";
import { useNavigate } from "react-router-dom";

const PartnerAgencies: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<PartnerCompaniesFields>({
    defaultValues: {
      inviteModal: false,
      isSubmitting: false,
      data: [],
      isLoading: true,
      selectedAgents: [],
      jobs: [],
      selectedJobs: [],
    },
  });
  const fields = watch();

  useEffect(() => {
    fetchData();
    fetchJobRequirements();
  }, []);

  const fetchJobRequirements = useCallback(async () => {
    try {
      const response = await getJobRequirement();
      setValue(
        "jobs",
        response?.data?.filter((x) => x?.status === 2)
      );
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchData = async () => {
    try {
      const response = await getPartnerAgencies();
      setValue("data", response?.data);
    } catch (error) {
    } finally {
      setValue("isLoading", false);
    }
  };

  const filteredData = useMemo(() => {
    if (isNullOrEmpty(fields?.search)) {
      return fields?.data;
    }

    return fields?.data?.filter(
      (x) =>
        x.recruiter.firstName
          ?.toLowerCase()
          ?.includes(fields?.search?.trim()?.toLowerCase()) ||
        x.recruiter.refNo
          ?.toLowerCase()
          ?.includes(fields?.search?.trim()?.toLowerCase())
    );
  }, [fields?.data, fields?.search]);

  const filteredJobData = useMemo(() => {
    if (isNullOrEmpty(fields?.jobSearch)) {
      return fields?.jobs;
    }

    return fields?.jobs?.filter((x) =>
      x.title?.toLowerCase()?.includes(fields?.jobSearch?.trim()?.toLowerCase())
    );
  }, [fields?.jobs, fields?.jobSearch]);

  const sendJob = async () => {
    try {
      setValue("isSubmitting", true);
      const payload = {
        jobRequirementIds: fields?.selectedJobs,
        recruiterIds: fields?.selectedAgents,
      };
      await grantAccess(payload);
      setValue("inviteModal", false);
      setValue("openSucessModel", true);
    } catch (error) {
      console.error(error, "error");
      enqueueSnackbar(error, {
        variant: "error",
      });
    } finally {
      setValue("isSubmitting", false);
    }
  };

  if (fields?.isLoading && isNullOrEmpty(filteredData)) {
    return (
      <div className="flex flex-col rounded-lg bg-white w-full flex-grow p-6 justify-center items-center">
        <LoadingAnimation color={colors.PrimaryBlack} />
      </div>
    );
  }

  const formatNumber = (num: string | number) => {
    return Number(num) > 9 ? num : num.toString().padStart(2, "0");
  };

  const handleItemClick = (data: PartnerCompany) => {
    navigate(`/recruitment-agents/${data?.recruiterId}`);
  };

  return (
    <div className="flex flex-1">
      {!isNullOrEmpty(fields?.data) ? (
        <div className="w-[100%] overflow-x-auto no-scrollbar">
          <div className="flex  items-center justify-between w-full gap-8 bg-[#F1F5F9] rounded-lg px-3 py-3 my-5">
            <div className="relative w-1/3">
              {/* @ts-ignore */}
              <CustomInput
                name="search"
                control={control}
                className="border-none"
                errors={errors}
                placeholder="Search by agent name, reference..."
                renderRight={<Search color="#6F6F6F" width={16} />}
              />
            </div>
            <button
              className={`bg-[#4D4D4D] text-white px-3 py-[10px] font-semibold rounded-md text-[10px] ${
                fields?.selectedAgents?.length === 0
                  ? "opacity-50"
                  : "opacity-100"
              }`}
              onClick={() => setValue("inviteModal", !fields?.inviteModal)}
              disabled={fields?.selectedAgents?.length === 0}
            >
              Select job requirement
            </button>
          </div>
          {!isNullOrEmpty(filteredData) ? (
            <table className="min-w-full text-xs text-left">
              <thead className="border-b border-[#E0E0E0]">
                <tr className="font-semibold">
                  <th className="text-xs font-bold text-[#6F6F6F]"></th>
                  <th className="py-3 text-xs font-bold text-[#6F6F6F]">
                    Recruitment Agent
                  </th>
                  <th className="py-3 text-xs font-bold text-[#6F6F6F]">
                    Company
                  </th>
                  <th className="py-3 text-xs font-bold text-[#6F6F6F] text-center">
                    Open Jobs
                  </th>
                  <th className="py-3 text-xs font-bold text-[#6F6F6F] ">
                    Working since
                  </th>
                  <th className="py-3 text-xs font-bold text-[#6F6F6F]">
                    Location
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {filteredData?.map((agent, idx) => (
                  <tr
                    key={idx}
                    className="border-t border-gray-100 cursor-pointer hover:bg-gray-50"
                  >
                    <td>
                      <div
                        className={`flex ml-2  border border-primary items-center justify-center rounded h-[18px] w-[18px] hover:bg-gray-200 ${
                          fields.selectedAgents?.includes(agent?.recruiterId) &&
                          "bg-primary hover:bg-light-black"
                        }`}
                        role="button"
                        onClick={() => {
                          if (
                            fields.selectedAgents?.includes(agent?.recruiterId)
                          ) {
                            setValue(
                              "selectedAgents",
                              fields?.selectedAgents?.filter(
                                (x) => x !== agent?.recruiterId
                              )
                            );
                          } else {
                            setValue("selectedAgents", [
                              ...fields.selectedAgents,
                              agent?.recruiterId,
                            ]);
                          }
                        }}
                      >
                        {fields.selectedAgents?.includes(
                          agent?.recruiterId
                        ) && (
                          <Check width={20} height={20} color={colors.White} />
                        )}
                      </div>
                    </td>
                    <td
                      onClick={() => handleItemClick(agent)}
                      className="text-xs font-medium text-[#6F6F6F]"
                    >
                      <div className="flex gap-2 items-center">
                        <div
                          className={`h-8 w-8 rounded-full justify-center items-center flex bg-nwhited ${
                            !agent?.recruiter?.profilePic && "bg-[#E1E0E0]"
                          }`}
                        >
                          {agent?.recruiter?.profilePic ? (
                            <img
                              src={agent?.recruiter?.profilePic}
                              className="h-full w-full object-cover rounded-full"
                              alt="hiringCompany_profilePic"
                            />
                          ) : (
                            <span className="font-montserrat text-white font-semibold text-sm uppercase">
                              {agent?.recruiter?.firstName?.charAt(0)}
                            </span>
                          )}
                        </div>
                        {agent?.recruiter?.firstName}{" "}
                        {agent?.recruiter?.lastName}
                      </div>
                    </td>
                    <td
                      onClick={() => handleItemClick(agent)}
                      className="py-6 text-xs font-medium text-[#6F6F6F]"
                    >
                      {agent?.agentCompany?.name}
                    </td>
                    <td
                      onClick={() => handleItemClick(agent)}
                      className="py-6 text-xs text-[#6F6F6F] text-center"
                    >
                      {formatNumber(agent?.totalOpenJobs)}
                    </td>
                    <td
                      onClick={() => handleItemClick(agent)}
                      className="py-6 text-xs font-medium text-[#6F6F6F]"
                    >
                      {dayjs(agent?.recruiter?.createdAt).format(
                        "MMMM DD, YYYY"
                      )}
                    </td>
                    <td
                      onClick={() => handleItemClick(agent)}
                      className="py-6 text-xs text-[#6F6F6F]"
                    >
                      {isNullOrEmpty(agent.recruiter?.location)
                        ? "-"
                        : agent.recruiter?.location}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            isNullOrEmpty(filteredData) && (
              <div className="flex flex-col items-center justify-center flex-1 text-center">
                <img src={FileIcon} alt="File Icon" className="w-48 h-48" />
                <p className="text-xs text-[#6F6F6F] font-medium mt-4">
                  No search result found!
                </p>
              </div>
            )
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center flex-1 text-center">
          <img src={FileIcon} alt="File Icon" className="w-48 h-48" />
          <p className="text-xs text-[#6F6F6F] font-medium mt-4">
            You currently don’t have any patterned company.
          </p>
          <p className="text-sm text-charcoal font-semibold mt-6 whitespace-pre">
            Click here to invite.
          </p>
          <button
            className="flex items-center justify-center border bg-green-600 mt-3 rounded-lg px-4 py-2 gap-1 text-white font-semibold text-sm"
            onClick={() => setValue("inviteModal", !fields?.inviteModal)}
          >
            <Plus width={18} height={18} /> Invite
          </button>
        </div>
      )}

      {fields?.inviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg w-1/3 h-2/3 overflow-hidden">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-sm font-bold">Select job requirement</h2>
              <button onClick={() => setValue("inviteModal", false)}>
                <CrossBig width={24} height={24} />
              </button>
            </div>

            {isNullOrEmpty(fields?.jobs) ? (
              <div className="flex-col items-center justify-center flex-1 text-center">
                <p className="text-xs text-[#6F6F6F] font-medium">
                  No job found
                </p>
              </div>
            ) : (
              <div className="flex flex-1 flex-col h-[100%]">
                <div className="flex  items-center justify-between w-full gap-8 bg-[#F1F5F9] rounded-lg px-3 py-3 mb-5">
                  <div className="relative w-1/2">
                    {/* @ts-ignore */}
                    <CustomInput
                      name="jobSearch"
                      control={control}
                      className="border-none"
                      errors={errors}
                      placeholder="Search by job title"
                      renderRight={<Search color="#6F6F6F" width={16} />}
                    />
                  </div>
                  <button
                    className={`bg-[#4D4D4D] items-center flex text-white px-3 py-[10px] font-semibold rounded-md text-[10px] ${
                      fields?.selectedJobs?.length === 0
                        ? "opacity-50"
                        : "opacity-100"
                    }`}
                    disabled={
                      fields?.selectedJobs?.length === 0 || fields.isSubmitting
                    }
                    onClick={sendJob}
                  >
                    {fields.isSubmitting && (
                      <LoadingAnimation color={colors.White} />
                    )}
                    Send job requirement
                  </button>
                </div>
                <div className="flex h-[100%] overflow-y-scroll no-scrollbar">
                  {isNullOrEmpty(filteredJobData) ? (
                    <div className="flex-col items-center justify-center flex-1 text-center">
                      <p className="text-xs text-[#6F6F6F] font-medium">
                        No search result found!
                      </p>
                    </div>
                  ) : (
                    <div className="w-[100%]">
                      <table className="min-w-full text-xs text-left table-fixed">
                        <thead className="border-b border-[#E0E0E0]">
                          <tr className="font-semibold">
                            <th className="text-xs font-bold text-[#6F6F6F] w-[5%]"></th>
                            <th className="py-3 text-xs font-bold text-[#6F6F6F] w-[50%]">
                              Job title
                            </th>
                            <th className="py-3 text-xs font-bold text-[#6F6F6F] w-[25%]">
                              Created date
                            </th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-700">
                          {filteredJobData?.map((job, idx) => (
                            <tr
                              key={idx}
                              className="border-t border-gray-100 cursor-pointer hover:bg-gray-50"
                              role="button"
                              onClick={() => {
                                if (fields.selectedJobs?.includes(job?._id)) {
                                  setValue(
                                    "selectedJobs",
                                    fields?.selectedJobs?.filter(
                                      (x) => x !== job?._id
                                    )
                                  );
                                } else {
                                  setValue("selectedJobs", [
                                    ...fields.selectedJobs,
                                    job?._id,
                                  ]);
                                }
                              }}
                            >
                              <td>
                                <div
                                  className={`flex ml-2 border border-primary items-center justify-center rounded h-[18px] w-[18px] hover:bg-gray-200 ${
                                    fields.selectedJobs?.includes(job?._id) &&
                                    "bg-primary hover:bg-light-black"
                                  }`}
                                  role="button"
                                >
                                  {fields.selectedJobs?.includes(job?._id) && (
                                    <Check
                                      width={20}
                                      height={20}
                                      color={colors.White}
                                    />
                                  )}
                                </div>
                              </td>

                              {/* Job Title column taking up 50% width */}
                              <td className="py-6 text-xs text-[#6F6F6F] w-[50%]">
                                {job?.title}
                              </td>

                              <td className="py-6 text-xs font-medium text-[#6F6F6F] w-[25%]">
                                {dayjs(job?.createdAt).format("MMMM DD, YYYY")}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {fields?.openSucessModel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="flex  flex-col bg-white p-6 rounded-lg w-1/3 h-2/3 overflow-hidden">
            <div className="flex justify-end items-center mb-8">
              <button
                onClick={() =>
                  setValue("openSucessModel", !fields.openSucessModel)
                }
              >
                <CrossBig width={24} height={24} />
              </button>
            </div>
            <div className="flex justify-center mt-14">
              <Success />
            </div>
            <div className="font-semibold text-xl text-charcoal text-center mt-8">
              Successfully sent!
            </div>
            <div className="font-medium text-xs text-light-black text-center mt-2">
              The job requirement has been successfully sent to the following
              recruiter agencies:
            </div>
            <button
              className={`bg-[#4D4D4D] flex mt-11 self-center items-center text-white px-3 py-[10px] font-semibold rounded-md text-[10px]`}
              onClick={() =>
                setValue("openSucessModel", !fields.openSucessModel)
              }
            >
              Back to partner agencies list
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerAgencies;
